// Per-user theming token schema.
//
// Every themeable attribute of the panel is declared here as a token backed by
// a CSS custom property. The base values live in `globals.css` (`@theme`); the
// theming engine overrides any of them at runtime by setting the matching
// custom property on `<html>`, which cascades to every Tailwind utility and
// component that reads it — affecting ONLY the current user's view.
//
// To add a new themeable attribute: add a token here, reference its `cssVar`
// from the relevant component/CSS, and the editor + provider pick it up
// automatically.

export type TokenType = "color" | "size" | "font" | "number" | "choice";

export type TokenGroup = "core" | "surfaces" | "console" | "fileExplorer" | "layout";

export interface TokenDef {
  /** Stable key used in stored user theme overrides. */
  key: string;
  /** CSS custom property name this token drives (without the `var()` wrapper). */
  cssVar: string;
  /** Human label shown in the theme editor. */
  label: string;
  group: TokenGroup;
  type: TokenType;
  /** Default value, kept in sync with `@theme` in globals.css. */
  default: string;
  choices?: {value: string; label: string}[];
  min?: number;
  max?: number;
}

export const TOKEN_GROUPS: Record<TokenGroup, string> = {
  layout: "Layout and navigation",
  core: "Core",
  surfaces: "Surfaces",
  console: "Console",
  fileExplorer: "File explorer",
};

export const TOKENS: TokenDef[] = [
 {key:"sidebarStyle",cssVar:"--sidebar-style",label:"Sidebar",group:"layout",type:"choice",default:"expanded",choices:[{value:"expanded",label:"Classic sidebar"},{value:"compact",label:"Compact icon rail"},{value:"floating",label:"Floating sidebar"}]},
 {key:"panelLayout",cssVar:"--panel-layout",label:"Panel layout",group:"layout",type:"choice",default:"classic",choices:[{value:"classic",label:"Classic workspace"},{value:"topbar",label:"Horizontal navigation"},{value:"focus",label:"Focused workspace"}]},
 {key:"density",cssVar:"--panel-density",label:"Spacing",group:"layout",type:"choice",default:"comfortable",choices:[{value:"compact",label:"Compact"},{value:"comfortable",label:"Comfortable"},{value:"spacious",label:"Spacious"}]},
 {key:"surfaceStyle",cssVar:"--surface-style",label:"Panel surfaces",group:"layout",type:"choice",default:"outlined",choices:[{value:"outlined",label:"Outlined cards"},{value:"flat",label:"Flat"},{value:"glass",label:"Glass"}]},
 {key:"serversView",cssVar:"--servers-view",label:"Server list",group:"layout",type:"choice",default:"table",choices:[{value:"table",label:"Table"},{value:"cards",label:"Cards"}]},
 {key:"contentWidth",cssVar:"--content-width",label:"Content width",group:"layout",type:"choice",default:"wide",choices:[{value:"wide",label:"Wide"},{value:"full",label:"Full width"},{value:"centered",label:"Centered"}]},
 {key:"sidebarWidth",cssVar:"--sidebar-width",label:"Sidebar width",group:"layout",type:"size",default:"224px",min:180,max:340},
 {key:"panelFont",cssVar:"--panel-font",label:"Panel font",group:"core",type:"font",default:"var(--font-sans)"},
 {key:"panelFontSize",cssVar:"--panel-font-size",label:"Panel text size",group:"core",type:"size",default:"13px",min:11,max:18},
 {key:"sidebarBg",cssVar:"--sidebar-bg",label:"Sidebar background",group:"surfaces",type:"color",default:"#141418"},
 {key:"topbarBg",cssVar:"--topbar-bg",label:"Header background",group:"surfaces",type:"color",default:"#0a0a0a"},

  // Core
  { key: "accent", cssVar: "--color-accent", label: "Accent", group: "core", type: "color", default: "#7000fd" },
  { key: "accentHover", cssVar: "--color-accent-hover", label: "Accent (hover)", group: "core", type: "color", default: "#8a2bff" },
  { key: "foreground", cssVar: "--color-foreground", label: "Text", group: "core", type: "color", default: "#ededed" },
  { key: "muted", cssVar: "--color-muted", label: "Muted text", group: "core", type: "color", default: "#a1a1aa" },
  { key: "radius", cssVar: "--radius", label: "Corner radius", group: "core", type: "size", default: "0.5rem" },

  // Surfaces
  { key: "background", cssVar: "--color-background", label: "Background", group: "surfaces", type: "color", default: "#0a0a0a" },
  { key: "surface", cssVar: "--color-surface", label: "Surface", group: "surfaces", type: "color", default: "#141418" },
  { key: "surface2", cssVar: "--color-surface-2", label: "Surface (raised)", group: "surfaces", type: "color", default: "#1c1c22" },
  { key: "border", cssVar: "--color-border", label: "Border", group: "surfaces", type: "color", default: "#26262d" },

  // Console
  { key: "consoleBg", cssVar: "--console-bg", label: "Console background", group: "console", type: "color", default: "#0a0a0a" },
  { key: "consoleText", cssVar: "--console-text", label: "Console text", group: "console", type: "color", default: "#ededed" },
  { key: "consoleFont", cssVar: "--console-font", label: "Console font", group: "console", type: "font", default: "var(--font-mono)" },
  { key: "consoleFontSize", cssVar: "--console-font-size", label: "Console font size", group: "console", type: "size", default: "0.8125rem" },
  { key: "consoleLineHeight", cssVar: "--console-line-height", label: "Console line height", group: "console", type: "number", default: "1.6" },

  // File explorer
  { key: "fxBg", cssVar: "--fx-bg", label: "Explorer background", group: "fileExplorer", type: "color", default: "#141418" },
  { key: "fxRowHover", cssVar: "--fx-row-hover", label: "Row hover", group: "fileExplorer", type: "color", default: "#1c1c22" },
  { key: "fxIcon", cssVar: "--fx-icon", label: "Icon color", group: "fileExplorer", type: "color", default: "#7000fd" },
  { key: "fxFont", cssVar: "--fx-font", label: "Explorer font", group: "fileExplorer", type: "font", default: "var(--font-sans)" },
];

/** Stored per-user overrides: token key -> value. Missing keys fall back to defaults. */
export type ThemeOverrides = Record<string, string>;

const TOKENS_BY_KEY: Record<string, TokenDef> = Object.fromEntries(
  TOKENS.map((t) => [t.key, t]),
);

/** Maps stored overrides to the CSS custom properties to set on `<html>`. */
export function overridesToCssVars(overrides: ThemeOverrides): Record<string, string> {
  const vars: Record<string, string> = {};
  for (const [key, value] of Object.entries(overrides)) {
    const token = TOKENS_BY_KEY[key];
    if (token && value) vars[token.cssVar] = value;
  }
  return vars;
}

/** Full resolved value map (defaults merged with overrides), keyed by token key. */
export function resolveTheme(overrides: ThemeOverrides): ThemeOverrides {
  const resolved: ThemeOverrides = {};
  for (const token of TOKENS) {
    resolved[token.key] = overrides[token.key] ?? token.default;
  }
  return resolved;
}
